import useAuth from '@app/auth/use-auth';
import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import { totalEmissionsAxisProps } from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import { useAccount } from '@shared/api';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import { ChartConfig } from '@shared/components/ui/chart';
import { getCurrencySymbol } from '@shared/lib/format';
import { cn, formatUnderscoreText } from '@shared/lib/utils';

interface PackagingProps extends ReportSectionComponent {}

export default function Packaging({ reportType }: PackagingProps) {
  const {
    result: { packaging },
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);
  const totalSpend =
    packaging?.totalSpend != null ? Number(packaging.totalSpend) : null;
  const chartData = packaging?.emissionsByCategory.map((item) => ({
    category: formatUnderscoreText(item.category),
    emissions: item.totalEmissionsTonnes,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...(chartData?.map((d) => d.emissions) ?? [])
  );

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'var(--chart-2)',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  const data = {
    totalPackagingEmissions: packaging?.totalEmissionsTonnes,
    totalPackagingPurchased: (packaging?.totalWeightKg ?? 0) / 1000,
    overallCarbonIntensity: packaging?.averageCarbonIntensity,
    rating: packaging?.carbonIntensityRating,
  };

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          To calculate the total emissions from your {reportTypeCopy.past}{' '}
          packaging, each item you {reportTypeCopy.past} is matched to a
          standard item in our database, which can be comprised of multiple
          materials. The total emissions for each item is the overall emission
          factor for the item multiplied by the quantity {reportTypeCopy.past}.
        </Text>
        <ReportMetric
          title="Total packaging emissions"
          value={data.totalPackagingEmissions ?? 0}
          unit="tCO2e"
          size="lg"
          className="flex-1"
        />

        <div className="flex space-x-2">
          <ReportMetric
            title={`Total packaging ${reportTypeCopy.past}`}
            value={data.totalPackagingPurchased ?? 0}
            unit="tonnes"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="Overall carbon intensity"
            value={data.overallCarbonIntensity ?? 0}
            unit="kgCO2e/kg"
            size="md"
            leftAdornment={
              <div
                className={cn('size-6 shrink-0 rounded-sm mt-0.5 ')}
                style={{
                  backgroundColor:
                    PACKAGING_ITEMS_RATING[
                      data.rating as keyof typeof PACKAGING_ITEMS_RATING
                    ].color,
                }}
              ></div>
            }
            className="flex-1"
          />
        </div>
        {hasCostFeature && totalSpend != null && (
          <ReportMetric
            title="Total packaging spend"
            value={totalSpend}
            unit={getCurrencySymbol(account?.defaultCurrency)}
            size="md"
            unitClassName="text-4xl text-gray-400 font-medium order-first"
          />
        )}
      </>
      <>
        <Text variant="h3">Emissions by category</Text>
        <Text variant="reportBody">
          Every standard packaging item is classified into one of six
          categories. The chart below shows your overall emissions for each of
          these categories.
        </Text>

        <SimpleBarChart
          nameKey="category"
          dataKey="emissions"
          chartData={chartData}
          chartConfig={chartConfig}
          className="h-max max-h-[calc(var(--content-title-height)-20rem)] w-full flex-1"
          margin={{ top: 50 }}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
        />
      </>
    </ReportContent>
  );
}
